import React, { useCallback, useState } from 'react'
import Grid from '@material-ui/core/Grid'
import PropTypes from 'prop-types'
import { Controller, useForm } from 'react-hook-form'
import { Alert } from '@material-ui/lab'
import SydButton from '../../../../../commonDesign/Button'
import SydModalActions from '../../../../../commonDesign/SydModal/SydModalActions'
import SydLabel, { hookFormErrorAdapter } from '../../../../../commonDesign/SydLabel'
import SydInput from '../../../../../commonDesign/SydInput'
import { decipherError } from '../../../../../../utils/decipherError'
import { dataCatalog } from '../../../../../../api/coreData'

const useSubmitter = (form, onComplete) => {
  const { handleSubmit } = form
  const [processing, setProcessing] = useState(false)
  const [error, setError] = useState(null)
  const { mutateAsync: internalMapTableToCatalog } = dataCatalog.useInternalMapTableToCatalog()
  const onSubmit = useCallback(async (formData) => {
    const command = {
      schemaName: formData.schemaName || null,
      tableName: formData.tableName || null
    }

    try {
      setError(null)
      setProcessing(true)

      const result = await internalMapTableToCatalog(command)
      if (result?.statusCode === 500) {
        setError('Something unexpected happened while mapping the table into the catalog')
        return
      }
      onComplete(result)
    } catch (err) {
      setError(decipherError(err))
    } finally {
      setProcessing(false)
    }
  }, [setProcessing, onComplete, setError, internalMapTableToCatalog])

  const submitter = useCallback(async (e) => {
    const onValid = async (form) => {
      await onSubmit(form)
    }
    const onInvalid = (errors) => {
      console.error(errors)
    }

    const handler = handleSubmit(onValid, onInvalid)
    await handler(e)
  }, [handleSubmit, onSubmit])

  return {
    submitter,
    processing,
    error
  }
}

function MapTableForm ({ onCancel, onComplete }) {
  const form = useForm({
    mode: 'onChange',
    defaultValues: {
      schemaName: 'dataExport',
      tableName: ''
    }
  })

  const { submitter, processing, error } = useSubmitter(form, onComplete)

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Alert severity='warning'>This action is only available to internal employees</Alert>
      </Grid>
      <Grid item xs={6}>
        <Controller
          name='schemaName'
          control={form.control}
          rules={{
            required: { value: true, message: 'Required.' },
            maxLength: { value: 64, message: 'Max 64 Characters.' }
          }}
          render={(f) => (
            <SydLabel label='Schema Name' description='' error={hookFormErrorAdapter(f, f.fieldState)} required>
              <SydInput {...f.field} />
            </SydLabel>
          )}
        />
      </Grid>
      <Grid item xs={6}>
        <Controller
          name='tableName'
          control={form.control}
          rules={{
            required: { value: true, message: 'Required.' },
            maxLength: { value: 256, message: 'Max 256 Characters.' }
          }}
          render={(f) => (
            <SydLabel label='Table Name' description='' error={hookFormErrorAdapter(f, f.fieldState)} required>
              <SydInput {...f.field} autoFocus />
            </SydLabel>
          )}
        />
      </Grid>
      <Grid item xs={12}>
        <SydModalActions>
          {error ? <div className='__error'>{error}</div> : null}
          <SydButton disabled={processing} variant='ghost' size='md' onClick={onCancel}>Cancel</SydButton>
          <SydButton
            processing={processing}
            variant='primary'
            size='md'
            onClick={submitter}
            disabled={!form.formState.isValid}
          >
            Save
          </SydButton>
        </SydModalActions>
      </Grid>
    </Grid>
  )
}

MapTableForm.propTypes = {
  onComplete: PropTypes.func,
  onCancel: PropTypes.func
}

export default MapTableForm
